import { useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { StyleAdherence } from "@/features/studio-ai/types/studio-ai.types"

import { Check, ChevronDown } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  getStyleTemplateErrorMessage,
  useStyleTemplates,
} from "@/features/studio-ai/hooks/use-style-templates"
import { MediaType, VideoStyle } from "@/features/studio-ai/types/studio-ai.types"
import { cn } from "@/lib/utils"

type StyleStepProps = {
  selected: VideoStyle
  onSelect: (style: VideoStyle) => void
  mediaType?: MediaType
  selectedTemplateId?: string | null
  onTemplateSelect?: (templateId: string | null) => void
  selectedAdherence?: StyleAdherence
  onAdherenceSelect?: (adherence: StyleAdherence) => void
}

const ADHERENCE_OPTIONS: Array<{ value: StyleAdherence; labelKey: string; descKey: string }> = [
  { value: "inspired", labelKey: "adherence.inspired", descKey: "adherence.inspiredDesc" },
  { value: "balanced", labelKey: "adherence.balanced", descKey: "adherence.balancedDesc" },
  { value: "strict", labelKey: "adherence.strict", descKey: "adherence.strictDesc" },
]

const STYLE_OPTIONS = [
  { value: VideoStyle.Custom, labelKey: "custom", descKey: "customDesc" },
  { value: VideoStyle.Cinematic, labelKey: "cinematic", descKey: "cinematicDesc" },
  { value: VideoStyle.Realistic, labelKey: "realistic", descKey: "realisticDesc" },
  { value: VideoStyle.Animated, labelKey: "animated", descKey: "animatedDesc" },
] as const

export function StyleStep({
  selected,
  onSelect,
  mediaType,
  selectedTemplateId,
  onTemplateSelect,
  selectedAdherence = "inspired",
  onAdherenceSelect,
}: StyleStepProps) {
  const keyPrefix = mediaType === MediaType.Video ? "studioAi.video.style" : "studioAi.image.style"
  const { t } = useTranslation(undefined, { keyPrefix })

  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
    isError: isTemplateLoadError,
    error: templateLoadError,
    refetch: refetchTemplates,
  } = useStyleTemplates({
    media_type: mediaType === MediaType.Video ? "video" : "image",
  })
  const presets = useMemo(() => templates.filter((template) => template.is_default), [templates])
  const savedTemplates = useMemo(
    () => templates.filter((template) => !template.is_default),
    [templates]
  )
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates]
  )

  const hasTemplates = templates.length > 0

  useEffect(() => {
    if (selectedTemplateId && !selectedTemplate && onTemplateSelect) {
      onTemplateSelect(null)
    }
  }, [onTemplateSelect, selectedTemplate, selectedTemplateId])

  const handleTemplateSelect = (templateId: string | null) => {
    onTemplateSelect?.(templateId)
    // Auto-set recommended adherence when selecting a template
    if (templateId && onAdherenceSelect) {
      const template = templates.find((t) => t.id === templateId)
      if (template?.recommended_adherence) {
        onAdherenceSelect(template.recommended_adherence)
      } else {
        onAdherenceSelect("inspired")
      }
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {STYLE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(
              "flex flex-col items-start gap-1.5 rounded-xl border-2 p-4 text-left transition-[border-color,background-color,box-shadow]",
              "hover:border-primary/50 hover:shadow-sm",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              selected === option.value ? "border-primary bg-primary/5 shadow-sm" : "border-border"
            )}
            onClick={() => onSelect(option.value)}
          >
            <p className="text-sm font-semibold">{t(option.labelKey)}</p>
            <p className="text-muted-foreground text-xs">{t(option.descKey)}</p>
          </button>
        ))}
      </div>

      <div className="rounded-xl border p-3">
        <div className="mb-2 flex items-center gap-2">
          <p className="text-sm font-medium">{t("templateButton")}</p>
          {hasTemplates ? <Badge variant="secondary">{templates.length}</Badge> : null}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={selectedTemplate ? "default" : "outline"}
              className={cn(
                "w-full justify-between",
                hasTemplates ? "border-primary/40" : undefined
              )}
            >
              <span className="truncate">
                {selectedTemplate ? selectedTemplate.title : t("noTemplate")}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
            <DropdownMenuLabel>{t("availableTemplates")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleTemplateSelect(null)}>
              <Check
                className={cn("mr-2 h-4 w-4", !selectedTemplate ? "opacity-100" : "opacity-0")}
              />
              {t("noTemplate")}
            </DropdownMenuItem>
            {isLoadingTemplates && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  {t("loadingTemplates", { defaultValue: "Loading templates..." })}
                </DropdownMenuItem>
              </>
            )}
            {!isLoadingTemplates && isTemplateLoadError && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                  {getStyleTemplateErrorMessage(templateLoadError)}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => void refetchTemplates()}>
                  {t("retryLoadingTemplates", { defaultValue: "Retry loading templates" })}
                </DropdownMenuItem>
              </>
            )}
            {!isLoadingTemplates && !isTemplateLoadError && hasTemplates && (
              <>
                {presets.length > 0 ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>{t("templatesPresets")}</DropdownMenuLabel>
                    {presets.map((template) => (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => handleTemplateSelect(template.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedTemplateId === template.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{template.title}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : null}
                {savedTemplates.length > 0 ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>{t("templatesSaved")}</DropdownMenuLabel>
                    {savedTemplates.map((template) => (
                      <DropdownMenuItem
                        key={template.id}
                        onClick={() => handleTemplateSelect(template.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedTemplateId === template.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">{template.title}</span>
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : null}
              </>
            )}
            {!isLoadingTemplates && !isTemplateLoadError && !hasTemplates && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>{t("noTemplatesAvailable")}</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {selectedTemplate && onAdherenceSelect && (
        <div className="rounded-xl border p-3">
          <div className="mb-2">
            <p className="text-sm font-medium">{t("adherence.title")}</p>
            <p className="text-muted-foreground text-xs">{t("adherence.description")}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {ADHERENCE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors",
                  "hover:border-primary/50",
                  "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                  selectedAdherence === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border"
                )}
                onClick={() => onAdherenceSelect(option.value)}
              >
                <p className="text-sm font-medium">{t(option.labelKey)}</p>
                <p className="text-muted-foreground text-xs">{t(option.descKey)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
